import { AttackPattern, PatternState } from '../AttackPattern.js';

const HOLD_RING_AT = 1.1; // seconds into the hold when the pressure ring fires (phase 2+)
const ARM_GRACE = 0.35; // seconds after arming before straying can break the vow — room to dash back in

/**
 * The Vow (The Jealous Regent) — a promise extracted at your own feet. The
 * Regent drops a vow seal exactly where you stand and chains you to it:
 * keep within its small circle until the vow expires and the Regent is
 * MOVED (a vulnerability window opens); break it — step out early — and
 * the seal erupts in aimed lances from the spot you abandoned. Déjà Vu
 * punishes where you were; the Vow pins where you're ALLOWED to be next,
 * while everything else the Regent layers keeps playing around you. The
 * choice each cast: hold still through the storm for the reward window, or
 * deliberately break it early from behind cover and eat a burst you chose
 * the timing of.
 */
export class TheVow extends AttackPattern {
  constructor({ minPhase = 1, config }) {
    super({ name: 'The Vow', telegraphDuration: 0.9, activeDuration: 3.4, minPhase, arenaControl: true });
    this.config = config;
  }

  onBegin(_boss, player) {
    // Sealed where they stood at the moment of the telegraph's end? No —
    // at cast, so the telegraph honestly shows the circle they must return
    // to (or choose to abandon) before it arms.
    this._anchorX = player.x;
    this._anchorY = player.y;
    this._held = 0;
    this._resolved = false; // true once broken OR kept — either way the seal is spent
    this._broken = false;
    this._ringFired = false;
  }

  onActiveTick(dt, boss, player, pool) {
    if (this._resolved) return;
    this._held += dt;

    const dist = Math.hypot(player.x - this._anchorX, player.y - this._anchorY);
    if (dist > this.config.allowRadius + player.radius && this._held > ARM_GRACE) {
      this._break(boss, player, pool);
      return;
    }

    // Phase 2+: the Regent tests the vow — one slow ring washes over the
    // pinned circle mid-hold, dodged by moving WITHIN the allowance.
    if (boss.phase >= 2 && !this._ringFired && this._held >= HOLD_RING_AT) {
      this._ringFired = true;
      const count = 12;
      const baseAngle = Math.random() * Math.PI * 2;
      for (let i = 0; i < count; i++) {
        const angle = baseAngle + (i / count) * Math.PI * 2;
        pool.fire({
          x: boss.x,
          y: boss.y,
          vx: Math.cos(angle) * this.config.testRingSpeed,
          vy: Math.sin(angle) * this.config.testRingSpeed,
          radius: 2.5,
          damage: this.config.testRingDamage,
          color: '#ff87b3',
          lifetime: 4,
        });
      }
    }

    if (this._held >= this.config.holdDuration) {
      this._resolved = true;
      boss.eventBus?.emit('vow:kept');
      // The Regent is moved — the loyalty reward window.
      boss.openVulnerability(0.9, 1.6);
    }
  }

  _break(boss, player, pool) {
    this._resolved = true;
    this._broken = true;
    const count = this.config.lanceCount + boss.phase * 2;
    const aim = Math.atan2(player.y - this._anchorY, player.x - this._anchorX);
    // A fan biased at the deserter, plus a thin full circle so the eruption
    // reads as the SEAL detonating, not a turret shot.
    for (let i = 0; i < count; i++) {
      const angle = aim + (i / count - 0.5) * 0.9;
      pool.fire({
        x: this._anchorX,
        y: this._anchorY,
        vx: Math.cos(angle) * this.config.lanceSpeed,
        vy: Math.sin(angle) * this.config.lanceSpeed,
        radius: 2.4,
        damage: this.config.lanceDamage,
        color: '#ffb3cf',
        lifetime: 3,
      });
    }
    for (let i = 0; i < 8; i++) {
      const angle = (i / 8) * Math.PI * 2;
      pool.fire({
        x: this._anchorX,
        y: this._anchorY,
        vx: Math.cos(angle) * this.config.lanceSpeed * 0.55,
        vy: Math.sin(angle) * this.config.lanceSpeed * 0.55,
        radius: 2.4,
        damage: this.config.lanceDamage,
        color: '#ff87b3',
        lifetime: 3,
      });
    }
    boss.eventBus?.emit('vow:broken', { x: this._anchorX, y: this._anchorY });
  }

  getVisual(_boss) {
    if (this.state === PatternState.TELEGRAPH) {
      return {
        kind: 'vowAnchor',
        x: this._anchorX,
        y: this._anchorY,
        radius: this.config.allowRadius,
        t: 0,
        arming: 1 - this._timer / this.telegraphDuration,
        broken: false,
      };
    }
    if (this.state === PatternState.ACTIVE && !this._resolved) {
      return {
        kind: 'vowAnchor',
        x: this._anchorX,
        y: this._anchorY,
        radius: this.config.allowRadius,
        t: this._held / this.config.holdDuration,
        arming: 1,
        broken: false,
      };
    }
    return null;
  }
}
